import { configureStore } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { createBrowserHistory } from 'history';
import createSagaMiddleware from 'redux-saga';
import { searchMiddleware } from 'app/views/layout/views/search/search.middleware';
import { busylightMiddleware } from 'app/views/layout/views/settings/views/integrations/views/busylight/busylight.middleware';
import { actions as conversationActions } from 'components/conversation/actions/conversation';
import { actions as conversationsActions } from 'components/conversation/actions/conversations';
import { notificationActions } from 'components/notifications/actions/notification';
import { electronMiddleware } from 'middleware/electron';
import { keyboardShortcutsMiddleware } from 'middleware/keyboardShortcuts/middleware';
import { createSyncMiddleware } from 'middleware/sync';
import { twilioMiddleware } from 'middleware/twilio/middleware';
import { visibilityMiddleware } from 'middleware/visibility/middleware';
import { websocketMiddleware } from 'middleware/websocket/middleware';
import { api } from 'utils/apis/api';
import { elevioApiMiddleware } from './assets/components/conversation/components/integrations/elevio/src/utils/apis/graphql/api/graphQlApi';
import { createReducer } from './reducer';
import { sagas } from './sagas';
/** Creates a redux saga middleware. Make sure this is last in the middleware. */
const sagaMiddleware = createSagaMiddleware({
    // Saga's don't propagate errors.
    onError: (error, { sagaStack }) => {
        Sentry.captureException(error, { extra: { sagaStack } });
    },
});
/** Creates the sync middleware. */
const syncMiddleware = createSyncMiddleware({
    prepareState: (state) => ({
        conversations: state.conversations,
    }),
    actionPredicate: (action) => [
        notificationActions.notificationRemoveByType.toString(),
        conversationActions.conversationAddSubmittedMessage.toString(),
        conversationActions.conversationSubmitMessageSucceeded.toString(),
        conversationActions.conversationSubmitMessageFailed.toString(),
        conversationsActions.conversationsIncreasingUnread.toString(),
        conversationsActions.conversationsResetUnread.toString(),
        conversationsActions.conversationsAdd.toString(),
        conversationsActions.conversationsRemove.toString(),
        conversationsActions.conversationsUpdateUser.toString(),
        conversationsActions.conversationsUnassignSelf.toString(),
        conversationsActions.conversationsSaveTempMessage.toString(),
        conversationsActions.conversationsSaveTempWrapup.toString(),
        conversationsActions.conversationsCloseConversation.toString(),
        conversationsActions.conversationsConversationToPending.toString(),
        conversationsActions.conversationsConversationCancelPending.toString(),
        conversationsActions.conversationsConversationOfferAccepted.toString(),
        conversationsActions.conversationsConversationClaimed.toString(),
        conversationsActions.conversationsConversationTransfered.toString(),
        conversationsActions.conversationsConversationAssistedTransferAccepted.toString(),
    ].includes(action.type),
});
/** The history object. */
export const history = createBrowserHistory({
    window,
});
/** The enhanced store and subscribe router in debug env ie. for devtools. */
export const store = configureStore({
    reducer: createReducer(),
    middleware: (getDefaultMiddleware) => getDefaultMiddleware({
        // Temporarily disabled until everything has been converted to
        // redux-toolkit.
        serializableCheck: false,
        // Disabled as it slows down development build.
        immutableCheck: false,
    }).concat(api.middleware, ...elevioApiMiddleware, websocketMiddleware(), electronMiddleware(), busylightMiddleware(), keyboardShortcutsMiddleware(), twilioMiddleware(), visibilityMiddleware(), searchMiddleware(), syncMiddleware, sagaMiddleware),
});
sagaMiddleware.run(sagas);
